import TableOperationsUI from 'core-system/Table/TableOperationsUI'
import {
  filterRows,
  searchRows,
  sortRows,
} from 'core-system/Table/helperFunctions'
import {
  FilterCheckbox,
  FilterObject,
} from 'core-system/Table/FilterModel'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

const Bar = styled.div<{ $margin: string }>`
  margin: ${(props) => props.$margin};
`

const defaultApplyFilters = <Row,>(rows: Row[], filters: FilterCheckbox[]) =>
  filterRows(rows, filters) as Row[]

interface DrawerTableOperationsOptions<Row> {
  source: Row[]
  searchKeys: readonly string[]
  sortKey: string
  buildFilters: (rows: Row[]) => FilterObject[]
  applyFilters?: (rows: Row[], filters: FilterCheckbox[]) => Row[]
  margin?: string
}

export const useDrawerTableOperations = <Row,>({
  source,
  searchKeys,
  sortKey,
  buildFilters,
  applyFilters = defaultApplyFilters,
  margin = '0.75rem 0',
}: DrawerTableOperationsOptions<Row>) => {
  const [appliedSearch, setAppliedSearch] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<FilterCheckbox[]>([])
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  )
  const [searchValue, setSearchValue] = useState('')
  const [sourceRows, setSourceRows] = useState(source)

  const sourceChanged = sourceRows !== source
  if (sourceChanged) {
    setSourceRows(source)
    setAppliedSearch('')
    setAppliedFilters([])
    setSortDirection(null)
    setSearchValue('')
  }

  const search = sourceChanged ? '' : appliedSearch
  const filters = sourceChanged ? [] : appliedFilters
  const direction = sourceChanged ? null : sortDirection
  const filterOptions = useMemo(() => buildFilters(source), [buildFilters, source])

  const apply = useCallback(
    (rows: Row[]) => {
      let next = rows
      if (search) {
        next = searchRows(next, [...searchKeys], search) as Row[]
      }
      if (filters.length) {
        next = applyFilters(next, filters)
      }
      if (direction) {
        next = sortRows(next, sortKey, direction) as Row[]
      }
      return next
    },
    [search, searchKeys, filters, applyFilters, direction, sortKey]
  )

  const bar = (
    <Bar $margin={margin}>
      <TableOperationsUI
        handleSearch={(value) => setAppliedSearch(value)}
        handleSearchClear={() => {
          setAppliedSearch('')
          setSearchValue('')
        }}
        onAscendingClick={() => setSortDirection('asc')}
        onDescendingClick={() => setSortDirection('desc')}
        filters={filterOptions}
        handleApplyFilters={setAppliedFilters}
        handleFiltersReset={() => setAppliedFilters([])}
        isFilterButtonActive={filters.length > 0}
        activeSortDirection={direction}
        isSearchActive={Boolean(search)}
        searchValue={sourceChanged ? '' : searchValue}
        setSearchValue={setSearchValue}
      />
    </Bar>
  )

  return { apply, bar }
}
